import React from 'react';
import { motion } from 'framer-motion';
import { Box, Container, Typography, Grid, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { School, Work, Business } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { getFeatureFlags } from '@/config/featureFlags';

interface FeatureCardProps {
  clickable?: boolean;
}

const FeatureCard = styled(Box)<FeatureCardProps>`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  transition: all 0.3s ease;
  height: 100%;
  min-height: 320px;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: ${props => props.clickable ? 'translateY(-4px)' : 'none'};
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const IconWrapper = styled(Box)`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  margin-bottom: 8px;
`;

const features = [
  {
    icon: <School sx={{ fontSize: 32, color: '#6B46C1' }} />,
    title: 'For Students',
    description: 'Find volunteer opportunities that match your career interests and build your future.',
    details: 'Our AI-powered platform matches you with relevant opportunities, tracks your hours, and provides insights into your skill development.',
    link: '/how-it-works/#students'
  },
  {
    icon: <Work sx={{ fontSize: 32, color: '#4C51BF' }} />,
    title: 'For Educators',
    description: 'Streamline volunteer hour tracking and gain insights into student engagement.',
    details: 'Access digital verification tools, generate custom reports, and monitor student progress through an intuitive dashboard.',
    link: '/how-it-works/#teachers'
  },
  {
    icon: <Business sx={{ fontSize: 32, color: '#6B46C1' }} />,
    title: 'For Organizations',
    description: 'Connect with motivated students and manage your volunteer program efficiently.',
    details: 'Post opportunities, review applications, and track volunteer impact with our comprehensive management tools.',
    link: '/how-it-works/#employers'
  }
];

const FeatureSection: React.FC = () => {
  const featureFlags = getFeatureFlags();
  const isHowItWorksEnabled = featureFlags.enableHowItWorks;

  const renderFeatureContent = (feature: typeof features[0]) => (
    <FeatureCard clickable={isHowItWorksEnabled}>
      <IconWrapper>
        {feature.icon}
      </IconWrapper>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="h5"
          sx={{
            color: 'white',
            mb: 2,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            fontWeight: 600,
          }}
        >
          {feature.title}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255,255,255,0.9)',
            fontSize: { xs: '1rem', sm: '1.1rem' },
            lineHeight: 1.6,
            mb: 2,
            fontWeight: 500,
          }}
        >
          {feature.description}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255,255,255,0.7)',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            lineHeight: 1.6,
          }}
        >
          {feature.details}
        </Typography>
      </Box>
    </FeatureCard>
  );

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12, lg: 8 },
        background: 'linear-gradient(to bottom, #1a1133, #0a0a0a)',
        width: '100%',
      }}
    >
      <Container 
        maxWidth="lg"
        sx={{
          px: { xs: 3, sm: 4, md: 6 }
        }}
      >
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              {isHowItWorksEnabled ? (
                <Link
                  component={RouterLink}
                  to={feature.link}
                  sx={{ textDecoration: 'none' }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    {renderFeatureContent(feature)}
                  </motion.div>
                </Link>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  {renderFeatureContent(feature)}
                </motion.div>
              )}
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default FeatureSection; 